<template>
    <div class="company-information-form">
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.country_label')"
            :required="true"
            orientation="horizontal"
        >
            <CompassSelect v-model="selectedCountry" :filterable="true">
                <CompassDropdownOption
                    v-for="item in countries"
                    :key="item.code"
                    :label="item.name"
                    :value="item.code"
                    :flag="item.code.toLowerCase()"
                    type="flag"
                />
            </CompassSelect>
        </CompassFormItem>
        <SirenSearchBar v-if="isFrench" @select="handleResultSelected" />
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.siren_label')"
            :required="isFrench"
            orientation="horizontal"
            :error="isFrench ? errors.siren : ''"
        >
            <CompassInput
                v-model="siren"
                :error="isFrench && !!errors.siren"
                @input="errors.siren = ''"
                @blur="checkRequiredField('siren')"
            />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.siret_label')"
            :required="isFrench"
            orientation="horizontal"
            :error="isFrench ? errors.siret : ''"
        >
            <CompassInput
                v-model="siret"
                :error="isFrench && !!errors.siret"
                @input="errors.siret = ''"
                @blur="checkRequiredField('siret')"
            />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.company_name_label')"
            :required="isFrench"
            orientation="horizontal"
            :error="isFrench ? errors.legalName : ''"
        >
            <CompassInput
                v-model="legalName"
                :error="isFrench && !!errors.legalName"
                @input="errors.legalName = ''"
                @blur="checkRequiredField('legalName')"
            />
        </CompassFormItem>

        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.vat_number_label')"
            orientation="horizontal"
            :error="isFrench ? errors.vatNumber : ''"
            :info="$t.__('CompanyInformation.legal_information.vat_number_info')"
        >
            <CompassInput
                v-model="vatNumber"
                :error="isFrench && !!errors.vatNumber"
                @input="errors.vatNumber = ''"
            />
            <CompassButton
                v-if="errors.vatNumber"
                class="company-information-form__correct-vat-button"
                leftIcon="wand"
                size="small"
                type="tertiary"
                :label="$t.__('CompanyInformation.legal_information.fix_vat_button')"
                @click="fixVatNumber()"
            />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.legal_status_label')"
            orientation="horizontal"
        >
            <CompassInput v-model="legalStatus" />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.share_capital_label')"
            orientation="horizontal"
        >
            <CompassInput v-model="shareCapital">
                <template v-if="showCurrencyLeft" #prefix>
                    {{ numformat.currencysymbol }}
                </template>
                <template v-if="showCurrencyRight" #suffix>
                    {{ numformat.currencysymbol }}
                </template>
            </CompassInput>
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.naf_code_label')"
            orientation="horizontal"
        >
            <CompassInput v-model="naf" />
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('CompanyInformation.legal_information.rcs_label')"
            orientation="horizontal"
        >
            <CompassInput v-model="rcs" />
        </CompassFormItem>
        <p class="company-information-form__advice typographies-paragraph-small-bold">
            <CompassIcon icon="comment-fill" :size="CompassEnums.IconSize.SMALL" />
            {{ $t.__('CompanyInformation.form.advice') }}
        </p>
        <CompassButton
            class="company-information-form__save-button"
            :label="$t.__('CompanyInformation.legal_information.save_button')"
            :disabled="disableSubmit"
            :loading="isLoading"
            data-bot="settings-company-info_validate-legal-infos"
            @click="handleSubmit"
        />
    </div>
</template>
<script>
import { createNamespacedHelpers } from 'vuex';
import SirenSearchBar from 'vcomponents/common/SirenSearchBar/SirenSearchBar.vue';
import { updateAccountInformation, lockAccountInformation } from 'api/account/account';
import { useVATNumber } from 'vcomponents/../composables/useVATNumber';
const { mapState: corpMapState, mapActions: corpMapActions } = createNamespacedHelpers('corp');
const { mapActions: mapPopupPanelActions } = createNamespacedHelpers('popupPanel');

const { generateVATNumber } = useVATNumber();

export default {
    components: {
        SirenSearchBar,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
        countries: {
            type: Array,
            required: true,
        },
    },
    emits: ['update'],
    data() {
        return {
            selectedCountry: this.data.country_code || 'FR',
            siren: this.data.legal_france.siren || '',
            siret: this.data.legal_france.siret || '',
            legalName: this.data.legal_name || '',
            vatNumber: this.data.legal_france.vat || '',
            legalStatus: this.data.legal_france.company_type || '',
            shareCapital: this.data.capital || '',
            naf: this.data.legal_france.ape_naf_code || '',
            rcs: this.data.legal_france.rcs_immatriculation || '',
            errors: {
                siren: '',
                siret: '',
                legalName: '',
                vatNumber: '',
            },
            isLoading: false,
        };
    },
    computed: {
        ...corpMapState(['numformat']),
        showCurrencyLeft() {
            return this.numformat.currencypos === 'left' || this.numformat.currencypos === 'both';
        },
        showCurrencyRight() {
            return this.numformat.currencypos === 'right' || this.numformat.currencypos === 'both';
        },
        isFrench() {
            return ['FR', 'GP', 'MQ', 'RE'].includes(this.selectedCountry);
        },
        disableSubmit() {
            if (this.isFrench) {
                if (!this.siren || !this.siret || !this.legalName) {
                    return true;
                }
                return Object.values(this.errors).some(Boolean);
            }
            return false;
        },
    },
    watch: {
        isFrench(newVal) {
            if (!newVal) {
                this.errors = {
                    siren: '',
                    siret: '',
                    legalName: '',
                    vatNumber: '',
                };
            }
        },
    },
    created() {
        EventBus.on('company-information-lock', this.handleSave);
    },
    unmounted() {
        EventBus.off('company-information-lock', this.handleSave);
    },
    methods: {
        ...corpMapActions(['setCompanyInformationLocked']),
        ...mapPopupPanelActions(['open']),
        handleResultSelected(result) {
            this.siren = result.id;
            this.siret = result.siret;
            this.legalName = result.name;
            this.vatNumber = '';
            this.naf = result.job_code;
            this.errors = {
                ...this.errors,
                siren: '',
                siret: '',
                vatNumber: '',
            };

            if (window.pendo?.accountId) {
                window.pendo.track('companyLegalInfoDirectory > used');
            }
        },
        checkRequiredField(field) {
            if (this.isFrench && !this[field]) {
                this.errors[field] = this.$t.__('common.labels.this_field_is_required');
            } else {
                this.errors[field] = '';
            }
        },
        handleSubmit() {
            this.open({
                key: 'companyInformationLockDialog',
                props: {
                    country:
                        this.countries.find((country) => country.code === this.selectedCountry)
                            ?.name || '',
                    siren: this.siren,
                    siret: this.siret,
                    legalName: this.legalName,
                    vatNumber: this.vatNumber,
                    legalStatus: this.legalStatus,
                    shareCapital: this.shareCapital,
                    naf: this.naf,
                    rcs: this.rcs,
                },
            });
        },
        handleSave() {
            this.isLoading = true;

            const payload = {
                country_code: this.selectedCountry,
                legal_name: this.legalName,
                legal_france: {
                    siren: this.siren,
                    siret: this.siret,
                    vat: this.vatNumber,
                    company_type: this.legalStatus,
                    ape_naf_code: this.naf,
                    rcs_immatriculation: this.rcs,
                },
                capital: this.shareCapital,
            };

            updateAccountInformation(payload)
                .then((response) => {
                    this.$emit('update', response.data);
                    lockAccountInformation()
                        .then(() => {
                            this.setCompanyInformationLocked(true);
                            this.open({
                                key: 'companyInformationSuccessDialog',
                                props: {},
                            });
                            this.isLoading = false;
                        })
                        .catch(() => {
                            this.$pushToast({
                                type: 'error',
                                text: this.$t.__('CompanyInformation.legal_information.lock.error'),
                            });
                        });
                })
                .catch((error) => {
                    const apiError = error.response?.data?.error.details;

                    const keys = apiError ? Object.keys(apiError) : null;

                    const fieldMap = {
                        siren: 'siren',
                        siret: 'siret',
                        vat: 'vatNumber',
                        legal_name: 'legalName',
                    };
                    for (const key of keys) {
                        const shortenedKey = key.split('.').pop();
                        if (fieldMap[shortenedKey]) {
                            this.errors[fieldMap[shortenedKey]] = apiError[key];
                        }
                    }

                    this.$pushToast({
                        type: 'error',
                        text: this.$t.__('CompanyInformation.legal_information.save.error'),
                    });
                    this.isLoading = false;
                });
        },
        fixVatNumber() {
            this.vatNumber = generateVATNumber(this.selectedCountry, this.siren);
            if (!this.vatNumber) {
                this.errors.vatNumber = this.$t.__(
                    'CompanyInformation.legal_information.vat_number_generation_error',
                );
                return;
            }
            this.errors.vatNumber = '';
        },
    },
};
</script>
<style lang="scss" scoped>
.company-information-form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    &__correct-vat-button {
        align-self: start;
        order: 3;
        margin-top: var(--spacing-0);
    }

    &__advice {
        color: var(--colors-common-text-subtle-default);
        display: flex;
        gap: var(--spacing-0);
        margin: 0;

        &:deep(.compass-icon) {
            color: var(--colors-common-icon-informative);
            flex-shrink: 0;
        }
    }

    &__save-button {
        display: block;
        margin-left: auto;
    }
}
</style>
